import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { sendApiRequest } from '../utils/api';
import LogoLoader from './LogoLoader';

const Dashboard = () => {
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await sendApiRequest('api/tests/results/history', {}, 'GET');
        setResults(response);
      } catch (err) {
        console.error('Error fetching results:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  // Calculate stats
  const totalTests = results.length;
  const avgSpeed = totalTests > 0 ? Math.round(results.reduce((sum, r) => sum + (r.wordsPerMinute || 0), 0) / totalTests) : 0;
  const avgAccuracy = totalTests > 0 ? Math.round(results.reduce((sum, r) => sum + (r.accuracy || 0), 0) / totalTests) : 0;
  const recentResults = results?.slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-indigo-800 mb-3">Welcome Back, {user?.name}!</h1>
          <p className="text-lg text-indigo-600">
            Track your progress and improve your stenography skills
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-12">
          {/* Total Tests Card */}
          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Tests</p>
                <p className="text-3xl font-bold text-indigo-800 mt-2">
                  {loading ? (
                    <span className="inline-block h-8 w-12 bg-gray-200 rounded animate-pulse"></span>
                  ) : (
                    totalTests
                  )}
                </p>
              </div>
              <div className="bg-indigo-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <div className="h-1 w-full bg-gray-100 rounded-full">
                <div
                  className="h-1 bg-indigo-500 rounded-full"
                  style={{ width: `${Math.min(100, totalTests)}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Average Speed Card */}
          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Average Speed</p>
                <p className="text-3xl font-bold text-indigo-800 mt-2">
                  {loading ? (
                    <span className="inline-block h-8 w-12 bg-gray-200 rounded animate-pulse"></span>
                  ) : (
                    `${avgSpeed} WPM`
                  )}
                </p>
              </div>
              <div className="bg-indigo-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">Your progress:</span>
                <span className={`text-sm font-medium ${avgSpeed > 60 ? 'text-green-500' : avgSpeed > 30 ? 'text-yellow-500' : 'text-red-500'}`}>
                  {avgSpeed > 60 ? 'Excellent' : avgSpeed > 30 ? 'Good' : 'Needs practice'}
                </span>
              </div>
            </div>
          </div>

          {/* Accuracy Card */}
          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Accuracy</p>
                <p className="text-3xl font-bold text-indigo-800 mt-2">
                  {loading ? (
                    <span className="inline-block h-8 w-12 bg-gray-200 rounded animate-pulse"></span>
                  ) : (
                    `${avgAccuracy}%`
                  )}
                </p>
              </div>
              <div className="bg-indigo-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full"
                    style={{ width: `${avgAccuracy}%` }}
                  ></div>
                </div>
                <span className="ml-2 text-sm text-gray-500">{avgAccuracy}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-100">Quick Actions</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/tests"
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border border-transparent hover:border-indigo-100"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-indigo-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-gray-800 font-medium">Take a Test</h3>
                  <p className="text-gray-500 text-sm">Practice your stenography skills</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-sm text-indigo-600 font-medium flex items-center">
                  Start now
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </Link>

            <Link
              to="/results"
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border border-transparent hover:border-indigo-100"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-indigo-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-gray-800 font-medium">View Results</h3>
                  <p className="text-gray-500 text-sm">Check your performance history</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-sm text-indigo-600 font-medium flex items-center">
                  View history
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </Link>

            <Link
              to="/profile"
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border border-transparent hover:border-indigo-100"
            >
              <div className="flex items-center space-x-4">
                <div className="bg-indigo-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-gray-800 font-medium">Profile</h3>
                  <p className="text-gray-500 text-sm">Manage your account settings</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="text-sm text-indigo-600 font-medium flex items-center">
                  Edit profile
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-100">Recent Activity</h2>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {loading ? (
              <LogoLoader />
            ) : recentResults.length === 0 ? (
              <div className="text-center p-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-700">No recent activity</h3>
                <p className="mt-1 text-gray-500">
                  Start practicing to see your progress here
                </p>
                <div className="mt-6">
                  <Link
                    to="/tests"
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Take a Test
                  </Link>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exam</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Accuracy</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Speed</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recentResults.map((result) => (
                      <tr key={result._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{result.test?.title || 'Untitled'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-indigo-600">{result.test?.exam?.name || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(result.date || result.completedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${result.accuracy >= 90 ? 'bg-green-100 text-green-800' : result.accuracy >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {result.accuracy}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${result.wordsPerMinute >= 60 ? 'bg-green-100 text-green-800' : result.wordsPerMinute >= 30 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                            {result.wordsPerMinute} WPM
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <button
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                            onClick={() => {
                              navigate(`/exam-result/${result.test?._id || 'unknown'}`, {
                                state: {
                                  stats: {
                                    correct: result.details?.correct,
                                    mistake: result.details?.mistake,
                                    missing: result.details?.missing,
                                    totalMistake: result.details?.totalMistake || result.mistakes,
                                    accuracy: result.accuracy,
                                    dictationSpeed: result.wordsPerMinute,
                                    totalWords: result.details?.refWords?.length || result.details?.totalWords,
                                    typedWords: result.details?.userWords?.length || result.details?.typedWords,
                                    time: result.timeTaken,
                                    date: new Date(result.date || result.completedAt).toLocaleDateString(),
                                    punctuation: result.punctuation || result.details?.punctuation || 0,
                                    userWords: result.details?.userWords,
                                    refWords: result.details?.refWords,
                                    comparison: result.details?.comparison || 0,
                                    detailedPenalties: result.details?.detailedPenalties || [],
                                    selectedRule: result.selectedRule || { name: 'Standard' },
                                  },
                                  userInput: result.typedText,
                                  reference: result.referenceText,
                                  audioUrl: result.audioUrl,
                                  topicName: result.test?.title || 'Practice Exercise',
                                },
                              });
                            }}
                            title="View Details"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;